import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Checkbox, Dialog, Input, Select, Textarea } from '@artosera/ui';
import { CATEGORIES_CONVERSATION, formatDate } from '@artosera/shared';
import { useTranslation } from 'react-i18next';
import {
  boiteApi,
  useReglagesBoite,
  type ReglagesBoite,
  type PreferencesBoite,
} from '../../api/boite-reception.js';
import { appelDeRequete, cleMetier } from '../../api/cles.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { NotFoundPage } from '../introuvable/NotFoundPage.js';
import { Field } from '../Field.js';
import { useErreurFormulaire } from '../useErreurFormulaire.js';
import { useGesteBoite } from './useGesteBoite.js';
import styles from './Boite.module.css';
export function ReglagesBoitePage() {
  const { galerie } = useShellContext();
  if (!galerieAModule(galerie, 'boite-reception') || !galerieALaction(galerie, 'inbox:read'))
    return <NotFoundPage />;
  return <Reglages />;
}
function Reglages() {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  const reglages = useReglagesBoite(galerieSlug);
  return (
    <section className={styles.page}>
      <h1>{t('regie:boiteReception.reglages')}</h1>
      {reglages.isLoading ? <p role="status">{t('commun:chargement')}</p> : null}
      {reglages.isError ? <p role="alert">{t('commun:erreurReseau')}</p> : null}
      {reglages.data ? <Formulaire initial={reglages.data.reglages} /> : null}
      <Preferences />
    </section>
  );
}
function Formulaire({ initial }: { initial: ReglagesBoite }) {
  const { galerieSlug, galerie } = useShellContext();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const ecrire = galerieALaction(galerie, 'inbox:settings');
  const [r, setR] = useState(initial);
  const [etiquettes, setEtiquettes] = useState(initial.etiquettes.join('\n'));
  const [ignores, setIgnores] = useState(initial.expediteursIgnores.join('\n'));
  const [precedent, setPrecedent] = useState(initial);
  if (precedent !== initial) {
    setPrecedent(initial);
    setR(initial);
    setEtiquettes(initial.etiquettes.join('\n'));
    setIgnores(initial.expediteursIgnores.join('\n'));
  }
  const [confirmation, setConfirmation] = useState(false);
  const [copie, setCopie] = useState(false);
  const erreurCopie = useErreurFormulaire();
  const regler = useGesteBoite((_: void, appel) =>
    boiteApi.regler(
      galerieSlug,
      {
        ...r,
        version: initial.version,
        etiquettes: etiquettes
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
        expediteursIgnores: ignores
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
      },
      appel,
    ),
  );
  const regenerer = useGesteBoite((_: void, appel) =>
    boiteApi.geste(galerieSlug, '/reglages/alias', { version: initial.version }, appel),
  );
  return (
    <>
      <section className={styles.bloc}>
        <h2>{t('regie:boiteReception.reception')}</h2>
        {initial.receptionDisponible && initial.adresseReception ? (
          <>
            <p>{initial.adresseReception}</p>
            <div className={styles.gestes}>
              <Button
                variant="secondary"
                onClick={() => {
                  erreurCopie.reinitialiser();
                  void navigator.clipboard
                    .writeText(initial.adresseReception!)
                    .then(() => setCopie(true))
                    .catch(erreurCopie.appliquer);
                }}
              >
                {t('regie:boiteReception.copier')}
              </Button>
              {ecrire ? (
                <Button variant="secondary" onClick={() => setConfirmation(true)}>
                  {t('regie:boiteReception.regenerer')}
                </Button>
              ) : null}
            </div>
            {copie ? <p role="status">{t('regie:boiteReception.copie')}</p> : null}
          </>
        ) : (
          <p>{t('regie:boiteReception.bientot')}</p>
        )}
        {erreurCopie.erreur ? <p role="alert">{erreurCopie.erreur}</p> : null}
        <h3>Gmail</h3>
        <p>{t('regie:boiteReception.gmail')}</p>
        <h3>Outlook</h3>
        <p>{t('regie:boiteReception.outlook')}</p>
        <p>
          {t('regie:boiteReception.dernierRecu', {
            date: initial.dernierRecuLe
              ? formatDate(new Date(initial.dernierRecuLe), i18n.language === 'en' ? 'en' : 'fr')
              : t('regie:boiteReception.jamais'),
          })}
        </p>
      </section>
      <form
        className={styles.formulaire}
        onSubmit={(e) => {
          e.preventDefault();
          void regler.executer();
        }}
      >
        <fieldset disabled={!ecrire || regler.pending} className={styles.formulaire}>
          <legend>{t('regie:boiteReception.reglagesGalerie')}</legend>
          <Field htmlFor="boite-adresse-reponse" label={t('regie:boiteReception.adresseReponse')}>
            <Select
              id="boite-adresse-reponse"
              value={r.adresseReponse}
              disabled={!ecrire || regler.pending}
              onValueChange={(v) =>
                setR({ ...r, adresseReponse: v as ReglagesBoite['adresseReponse'] })
              }
              options={[
                { value: 'reception', label: t('regie:boiteReception.adresseReception') },
                { value: 'galerie', label: t('regie:boiteReception.adresseGalerie') },
              ]}
            />
          </Field>
          <Field htmlFor="boite-signature" label={t('regie:boiteReception.signature')}>
            <Textarea
              id="boite-signature"
              maxLength={2000}
              value={r.signature}
              onChange={(e) => setR({ ...r, signature: e.target.value })}
            />
          </Field>
          <Field htmlFor="boite-etiquettes" label={t('regie:boiteReception.etiquettesReglages')}>
            <Textarea
              id="boite-etiquettes"
              value={etiquettes}
              onChange={(e) => setEtiquettes(e.target.value)}
            />
          </Field>
          <h2>{t('regie:boiteReception.objectifs')}</h2>
          {CATEGORIES_CONVERSATION.map((c) => (
            <Field
              key={c}
              htmlFor={`objectif-${c}`}
              label={t(`regie:boiteReception.categories.${c}`)}
            >
              <Input
                id={`objectif-${c}`}
                type="number"
                required
                min={1}
                max={720}
                step={1}
                value={r.objectifsHeures[c]}
                onChange={(e) =>
                  setR({
                    ...r,
                    objectifsHeures: { ...r.objectifsHeures, [c]: Number(e.target.value) },
                  })
                }
              />
            </Field>
          ))}
          <Checkbox
            id="boite-suivi"
            label={t('regie:boiteReception.suivi')}
            checked={r.suiviOuverture}
            onCheckedChange={(v) => setR({ ...r, suiviOuverture: v === true })}
            aria-describedby="boite-avertissement-suivi"
          />
          <p id="boite-avertissement-suivi">{t('regie:boiteReception.avertissementSuivi')}</p>
          <Field htmlFor="boite-ignores" label={t('regie:boiteReception.expediteursIgnores')}>
            <Textarea
              id="boite-ignores"
              value={ignores}
              onChange={(e) => setIgnores(e.target.value)}
            />
          </Field>
          {ecrire ? (
            <Button type="submit" loading={regler.pending}>
              {t('commun:actions.enregistrer')}
            </Button>
          ) : null}
        </fieldset>
        {regler.erreur ? <p role="alert">{regler.erreur}</p> : null}
      </form>
      <Dialog
        open={confirmation}
        onOpenChange={setConfirmation}
        title={t('regie:boiteReception.regenerer')}
        description={t('regie:boiteReception.confirmerRegeneration')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <Button
            loading={regenerer.pending}
            onClick={() => {
              void regenerer.executer().then((r) => {
                if (r) setConfirmation(false);
              });
            }}
          >
            {t('regie:boiteReception.confirmer')}
          </Button>
        }
      >
        {regenerer.erreur ? <p role="alert">{regenerer.erreur}</p> : null}
      </Dialog>
    </>
  );
}
function Preferences() {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  const preferences = useQuery({
    queryKey: cleMetier(galerieSlug, 'boite-reception', 'preferences'),
    queryFn: (c) => boiteApi.preferences(galerieSlug, appelDeRequete(c)),
  });
  return (
    <section className={styles.bloc}>
      <h2>{t('regie:boiteReception.preferences')}</h2>
      {preferences.data ? (
        <PreferencesFormulaire
          key={JSON.stringify(preferences.data.preferences)}
          initial={preferences.data.preferences}
        />
      ) : null}
      {preferences.isError ? <p role="alert">{t('commun:erreurReseau')}</p> : null}
    </section>
  );
}
function PreferencesFormulaire({ initial }: { initial: PreferencesBoite }) {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  const [p, setP] = useState(initial);
  const enregistrer = useGesteBoite((_: void, appel) => boiteApi.preferer(galerieSlug, p, appel));
  return (
    <form
      className={styles.formulaire}
      onSubmit={(e) => {
        e.preventDefault();
        void enregistrer.executer();
      }}
    >
      <Checkbox
        id="boite-resume"
        label={t('regie:boiteReception.resume')}
        checked={p.resumeQuotidien}
        onCheckedChange={(v) => setP({ ...p, resumeQuotidien: v === true })}
      />
      <Checkbox
        id="boite-alertes"
        label={t('regie:boiteReception.alertes')}
        checked={p.alertes}
        onCheckedChange={(v) => setP({ ...p, alertes: v === true })}
      />
      <Button type="submit" loading={enregistrer.pending}>
        {t('commun:actions.enregistrer')}
      </Button>
      {enregistrer.erreur ? <p role="alert">{enregistrer.erreur}</p> : null}
    </form>
  );
}
